import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { LoadingContext } from '../../context';
import Logo from '../../components/common/Logo';
import { PrimaryButton } from '../../components/common/ButtonsV2';
import { acceptInvitation } from '../../services/apiServices';
import LogoBlue from '../../assets/images/Logo-Blue.png';
import { validatePassword, doPasswordsMatch } from '../../utils/passwordUtils';
import PasswordInput from './PasswordInput';
import ConfirmPasswordInput from './ConfirmPasswordInput';
import AuthImage from '../Login/AuthImage';
import { authenticateWithToken } from '../../store/actions/authThunks';
import { CreatePasswordTextOverlay } from '../../assets/images';
import { useFeedbackContext } from '../../context';
import { ERROR_INVITATION_EXPIRED, ERROR_INVITATION_USED } from '../../constants';

const AcceptInvitation = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const navigate = useNavigate();
  const { setIsLoading } = useContext(LoadingContext);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const dispatch = useDispatch();

  const [password, setPassword] = useState('');
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));
  const [confirmPassword, setConfirmPassword] = useState('');

  const { showModal, hideModal } = useFeedbackContext();

  const handleNavigateHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    if (token === null) {
      showModal({
        variant: 'warning',
        title: 'It seems your invitation link is missing or invalid',
        copy: 'Please check your email or contact support if you need assistance.',
        buttonMessage: 'Understood',
        onClickButton: handleNavigateHome,
      });
    }
  }, [token, showModal, handleNavigateHome]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/facility-selection');
    }
  }, [isAuthenticated, navigate]);

  const showConfirmationModal = (access_token) => {
    showModal({
      variant: 'success',
      title: 'Your account has been successfully created!',
      copy: 'Please click the button below to log in.',
      buttonMessage: 'Log In Now',
      onClickButton: () => onClickLogin(access_token),
    });
  };

  const onClickLogin = async (accessToken) => {
    setIsLoading(true);
    try {
      const result = await dispatch(authenticateWithToken({ accessToken }));
      if (result.success) navigate('/');
    } catch (err) {
      const errorMessage =
        err?.response?.data?.detail || 'There was an error while trying to save your new password';
      showErrorModal(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const showErrorModal = (errorMessage) => {
    showModal({
      variant: 'warning',
      title: 'There was an error',
      copy: errorMessage,
      buttonMessage: 'Try Again',
      onClickButton: hideModal,
    });
  };

  const handleAcceptInvitationError = (errorMessage) => {
    if (errorMessage === ERROR_INVITATION_EXPIRED) {
      showModal({
        variant: 'warning',
        title: ERROR_INVITATION_EXPIRED,
        copy: 'This invitation link has expired. Please request a new invitation if needed.',
        buttonMessage: 'Go Back',
        onClickButton: hideModal,
      });
      return;
    }
    if (errorMessage === ERROR_INVITATION_USED) {
      showModal({
        variant: 'warning',
        title: ERROR_INVITATION_USED,
        copy: 'It looks like you have already set your password. Please log in to access your account.',
        buttonMessage: 'Go to Login',
        onClickButton: handleNavigateHome,
      });
      return;
    }
    showErrorModal(errorMessage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { access_token } = await acceptInvitation(token, password);
      showConfirmationModal(access_token);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.detail || 'There was an error while trying to save your new password';
      handleAcceptInvitationError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    const validatedPassword = validatePassword(newPassword);
    setPasswordValidation(validatedPassword);
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPassword = e.target.value;
    setConfirmPassword(confirmPassword);
  };

  const passwordsMatch = doPasswordsMatch(password, confirmPassword);
  const allRequirementsAreMet = (passwordValidation) =>
    Object.values(passwordValidation).every((requirement) => requirement === true);
  const isButtonDisabled = !(passwordsMatch && allRequirementsAreMet(passwordValidation));

  return (
    <div className="h-screen w-screen bg-custom-light-gray flex overflow-auto">
      <div className="flex-1 lg:grid lg:grid-cols-2 lg:gap-0 h-full">
        <AuthImage TextOverlaySrc={CreatePasswordTextOverlay} />

        <div className="flex flex-col items-center justify-center p-3 sm:p-4 md:p-6">
          <div className="w-full max-w-[450px]">
            <Logo
              URL={LogoBlue}
              name="JanWay Logo"
              variant="small"
              clickable={false}
              className="mb-4 mx-auto"
            />

            <h2 className="text-xl sm:text-2xl lg:text-3xl text-black font-bold text-center mb-6">
              Set up your new password
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <PasswordInput
                handlePasswordChange={handlePasswordChange}
                password={password}
                passwordValidation={passwordValidation}
              />

              <ConfirmPasswordInput
                password={password}
                confirmPassword={confirmPassword}
                handleConfirmPasswordChange={handleConfirmPasswordChange}
              />

              <div>
                <PrimaryButton type="submit" disabled={isButtonDisabled} fullWidth={true}>
                  Save Password
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
